import React, { useRef, useState } from 'react';
import { Typography, Box, Chip, Stack } from '@mui/material';

interface AnnotationRect {
  x: number;
  y: number;
  id: string;
  label: string;
  order: number;
  width: number;
  height: number;
}

interface AnnotationPoint {
  x: number;
  y: number;
  id: string;
  label: string;
  order: number;
}

interface CuboidCorners {
  tl: { x: number; y: number };
  tr: { x: number; y: number };
  bl: { x: number; y: number };
  br: { x: number; y: number };
}

interface AnnotationCuboid {
  id: string;
  front: CuboidCorners;
  back: CuboidCorners;
  label: string;
  order: number;
  direction: string;
}

interface AnnotationLinePoint {
  x: number;
  y: number;
  id: string;
}

interface AnnotationLine {
  id: string;
  type: string;
  label: string;
  order: number;
  points: AnnotationLinePoint[];
}

interface AnnotationPolygon {
  id: string;
  type: string;
  label: string;
  order: number;
  points: AnnotationLinePoint[];
}

interface LabelConfig {
  name: string;
  color: string;
  value: string;
  description?: string;
}

interface ImageAnnotationActionProps {
  actionValue?: any[];
}

function getColor(label: string, labelConfigs: LabelConfig[]): string {
  const found = labelConfigs.find((l) => l.value === label);
  return found?.color ?? '#1976d2';
}

function facePoints(corners: CuboidCorners): string {
  const { tl, tr, br, bl } = corners;
  return `${tl.x},${tl.y} ${tr.x},${tr.y} ${br.x},${br.y} ${bl.x},${bl.y}`;
}

interface AnnotatedImageProps {
  imageUrl: string;
  rects: AnnotationRect[];
  points: AnnotationPoint[];
  cuboids: AnnotationCuboid[];
  lines: AnnotationLine[];
  polygons: AnnotationPolygon[];
  labelConfigs: LabelConfig[];
}

function AnnotatedImage({ imageUrl, rects, points, cuboids, lines, polygons, labelConfigs }: AnnotatedImageProps) {
  const imgRef = useRef<HTMLImageElement>(null);
  const [naturalSize, setNaturalSize] = useState<{ w: number; h: number } | null>(null);

  const handleLoad = () => {
    if (imgRef.current) {
      setNaturalSize({
        w: imgRef.current.naturalWidth,
        h: imgRef.current.naturalHeight,
      });
    }
  };

  return (
    <Box sx={{ position: 'relative', display: 'inline-block', maxWidth: '100%' }}>
      <img
        ref={imgRef}
        src={imageUrl}
        alt="annotated"
        onLoad={handleLoad}
        style={{ display: 'block', maxWidth: '100%', maxHeight: '520px' }}
      />
      {naturalSize && (
        <svg
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            overflow: 'visible',
          }}
          viewBox={`0 0 ${naturalSize.w} ${naturalSize.h}`}
          preserveAspectRatio="xMidYMid meet"
        >
          {/* Rectangles */}
          {rects.map((rect) => {
            const color = getColor(rect.label, labelConfigs);
            return (
              <g key={rect.id}>
                <rect
                  x={rect.x}
                  y={rect.y}
                  width={rect.width}
                  height={rect.height}
                  fill={`${color}33`}
                  stroke={color}
                  strokeWidth={naturalSize.w * 0.004}
                />
                <text
                  x={rect.x + naturalSize.w * 0.005}
                  y={rect.y + naturalSize.w * 0.018}
                  fill={color}
                  fontSize={naturalSize.w * 0.02}
                  fontWeight="bold"
                >
                  {rect.order}
                </text>
              </g>
            );
          })}

          {/* Points */}
          {points.map((point) => {
            const color = getColor(point.label, labelConfigs);
            const r = naturalSize.w * 0.008;
            return (
              <g key={point.id}>
                <circle
                  cx={point.x}
                  cy={point.y}
                  r={r}
                  fill={color}
                  stroke="#fff"
                  strokeWidth={naturalSize.w * 0.002}
                />
                <text
                  x={point.x + r + naturalSize.w * 0.004}
                  y={point.y + naturalSize.w * 0.008}
                  fill={color}
                  fontSize={naturalSize.w * 0.02}
                  fontWeight="bold"
                >
                  {point.order}
                </text>
              </g>
            );
          })}

          {/* Lines */}
          {lines.map((line) => {
            const color = getColor(line.label, labelConfigs);
            const sw = naturalSize.w * 0.004;
            const pointsStr = line.points.map((p) => `${p.x},${p.y}`).join(' ');
            const first = line.points[0];
            return (
              <g key={line.id}>
                <polyline
                  points={pointsStr}
                  fill="none"
                  stroke={color}
                  strokeWidth={sw}
                  strokeLinejoin="round"
                  strokeLinecap="round"
                />
                {first && (
                  <text
                    x={first.x + naturalSize.w * 0.005}
                    y={first.y - naturalSize.w * 0.008}
                    fill={color}
                    fontSize={naturalSize.w * 0.02}
                    fontWeight="bold"
                  >
                    {line.order}
                  </text>
                )}
              </g>
            );
          })}

          {/* Polygons */}
          {polygons.map((polygon) => {
            const color = getColor(polygon.label, labelConfigs);
            const sw = naturalSize.w * 0.004;
            const pointsStr = polygon.points.map((p) => `${p.x},${p.y}`).join(' ');
            const first = polygon.points[0];
            return (
              <g key={polygon.id}>
                <polygon
                  points={pointsStr}
                  fill={`${color}33`}
                  stroke={color}
                  strokeWidth={sw}
                  strokeLinejoin="round"
                />
                {first && (
                  <text
                    x={first.x + naturalSize.w * 0.005}
                    y={first.y - naturalSize.w * 0.008}
                    fill={color}
                    fontSize={naturalSize.w * 0.02}
                    fontWeight="bold"
                  >
                    {polygon.order}
                  </text>
                )}
              </g>
            );
          })}

          {/* Cuboids */}
          {cuboids.map((cuboid) => {
            const color = getColor(cuboid.label, labelConfigs);
            const sw = naturalSize.w * 0.004;
            const { front, back } = cuboid;
            return (
              <g key={cuboid.id}>
                {/* Back face */}
                <polygon
                  points={facePoints(back)}
                  fill={`${color}18`}
                  stroke={color}
                  strokeWidth={sw}
                  strokeDasharray={`${sw * 4} ${sw * 2}`}
                />
                {/* Front face */}
                <polygon
                  points={facePoints(front)}
                  fill={`${color}33`}
                  stroke={color}
                  strokeWidth={sw}
                />
                {/* Connecting edges: front corner -> back corner */}
                {(['tl', 'tr', 'bl', 'br'] as const).map((corner) => (
                  <line
                    key={corner}
                    x1={front[corner].x}
                    y1={front[corner].y}
                    x2={back[corner].x}
                    y2={back[corner].y}
                    stroke={color}
                    strokeWidth={sw}
                  />
                ))}
                {/* Order label at front top-left */}
                <text
                  x={front.tl.x + naturalSize.w * 0.005}
                  y={front.tl.y + naturalSize.w * 0.018}
                  fill={color}
                  fontSize={naturalSize.w * 0.02}
                  fontWeight="bold"
                >
                  {cuboid.order}
                </text>
              </g>
            );
          })}
        </svg>
      )}
    </Box>
  );
}

export default function ImageAnnotationAction({ actionValue }: ImageAnnotationActionProps) {
  if (!actionValue || actionValue.length === 0) return null;

  return (
    <>
      {actionValue.map((action: any, index: number) => {
        const imageUrl: string = action?.config_details?.default_value?.trim() ?? '';
        const annotations = action?.annotations ?? {};
        const rects: AnnotationRect[] = annotations.rect ?? [];
        const points: AnnotationPoint[] = annotations.point ?? [];
        const cuboids: AnnotationCuboid[] = annotations.cuboid ?? [];
        const lines: AnnotationLine[] = annotations.line ?? [];
        const polygons: AnnotationPolygon[] = annotations.polygon ?? [];
        const labelConfigs: LabelConfig[] = action?.config_details?.value?.value ?? [];
        const heading: string =
          action?.config_details?.value?.label || 'Image Annotation';

        const totalAnnotations = rects.length + points.length + cuboids.length + lines.length + polygons.length;

        return (
          <Box key={index} sx={{ mb: 2 }}>
            <Typography sx={{ fontWeight: 'bold', mb: 1 }}>{heading}</Typography>

            {imageUrl ? (
              <AnnotatedImage
                imageUrl={imageUrl}
                rects={rects}
                points={points}
                cuboids={cuboids}
                lines={lines}
                polygons={polygons}
                labelConfigs={labelConfigs}
              />
            ) : (
              <Typography color="textSecondary">Image not available.</Typography>
            )}

            {/* Legend */}
            {labelConfigs.length > 0 && (
              <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', gap: 1 }}>
                {labelConfigs.map((lc, i) => (
                  <Chip
                    key={i}
                    label={lc.name}
                    size="small"
                    sx={{
                      backgroundColor: `${lc.color}22`,
                      border: `1.5px solid ${lc.color}`,
                      color: lc.color,
                      fontWeight: 600,
                    }}
                  />
                ))}
              </Stack>
            )}

            {/* Summary */}
            <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, display: 'block' }}>
              {[
                rects.length > 0 ? `${rects.length} rectangle${rects.length !== 1 ? 's' : ''}` : null,
                points.length > 0 ? `${points.length} point${points.length !== 1 ? 's' : ''}` : null,
                cuboids.length > 0 ? `${cuboids.length} cuboid${cuboids.length !== 1 ? 's' : ''}` : null,
                lines.length > 0 ? `${lines.length} line${lines.length !== 1 ? 's' : ''}` : null,
                polygons.length > 0 ? `${polygons.length} polygon${polygons.length !== 1 ? 's' : ''}` : null,
              ].filter(Boolean).join(' \u2022 ') || 'No annotations'}
            </Typography>
          </Box>
        );
      })}
    </>
  );
}
